// Shared recursive OU/account tree with checkboxes. Extracted verbatim from
// pages/OrgServices.js so the admin Policies page and the portal StackSets
// target picker render the identical tree. Props unchanged.
import { useState } from 'react';
import { Box, Checkbox, Chip, Collapse, IconButton, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import FolderIcon from '@mui/icons-material/FolderOpen';
import CloudIcon from '@mui/icons-material/Cloud';
import { CK, FONT_BODY, statusChipSx } from '../../pages/portal/portalStyles';

// Dense variant of the shared pastel-triad chip — the tree row is a compact
// line, so the chips keep the palette/typography but sit a little smaller.
const treeChipSx = (intent) => ({
  ...statusChipSx(intent),
  ml: 0.5,
  height: 20,
  fontSize: '0.65rem',
  flexShrink: 0,
});

const OrgTreeCheckbox = ({ node, depth = 0, selectedIds, onToggle, excludeIds = [], hideRoot = false, quotaMap = null }) => {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = node.children && node.children.length > 0;
  const isExcluded = excludeIds.includes(node.id);
  const isSelected = selectedIds.some((n) => n.id === node.id);
  const isHidden = hideRoot && node.type === 'ROOT';
  const quota = quotaMap ? quotaMap[node.id] : undefined;
  const atCapacity = quota !== undefined && quota >= 5;
  // Display-only normalization: the API returns both 'OU' and
  // 'ORGANIZATIONAL_UNIT' for the same kind of node, and they used to render
  // with different icons and chip colors. `node.type` itself is left untouched
  // so callers keep receiving the value they already handle.
  const kind = node.type === 'ORGANIZATIONAL_UNIT' ? 'OU' : node.type;

  const getIcon = () => {
    switch (kind) {
      case 'ROOT': return <BusinessIcon sx={{ fontSize: '1rem', color: CK.blue, flexShrink: 0 }} />;
      case 'OU': return <FolderIcon sx={{ fontSize: '1rem', color: CK.amber, flexShrink: 0 }} />;
      case 'ACCOUNT': return <CloudIcon sx={{ fontSize: '1rem', color: CK.green, flexShrink: 0 }} />;
      default: return null;
    }
  };

  return (
    <Box sx={{ ml: depth > 0 ? 3 : 0 }}>
      {!isHidden && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: 0.5,
            px: 0.5,
            minWidth: 0,
            borderRadius: '6px',
            transition: 'background-color 0.2s ease',
            // CK.hover (not pageBg): both StackSets call sites render this tree
            // inside an insetPanelSx panel whose background already IS pageBg,
            // so a pageBg hover would be invisible there.
            '&:hover': { backgroundColor: CK.hover },
          }}
        >
          {hasChildren ? (
            <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ mr: 0.5, p: 0.25, flexShrink: 0 }}>
              <ExpandMoreIcon
                sx={{
                  fontSize: '1rem',
                  transform: expanded ? 'none' : 'rotate(-90deg)',
                  transition: 'transform 0.2s ease',
                }}
              />
            </IconButton>
          ) : (
            <Box sx={{ width: 24, mr: 0.5, flexShrink: 0 }} />
          )}
          <Checkbox
            size="small"
            checked={isSelected}
            disabled={isExcluded || atCapacity}
            onChange={() => onToggle({ id: node.id, name: node.name, type: node.type })}
            sx={{ p: 0.25, mr: 0.5, flexShrink: 0 }}
          />
          {getIcon()}
          <Typography
            noWrap
            sx={{
              ml: 0.5,
              fontFamily: FONT_BODY,
              fontSize: '0.85rem',
              color: isExcluded ? CK.muted : CK.navy,
              minWidth: 0,
              flexShrink: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {node.name}
          </Typography>
          {kind === 'ACCOUNT' && node.id && (
            <Typography
              noWrap
              sx={{ ml: 0.5, fontFamily: 'monospace', fontSize: '0.75rem', color: CK.muted, flexShrink: 0 }}
            >
              {node.id}
            </Typography>
          )}
          <Chip
            label={kind}
            size="small"
            sx={treeChipSx(kind === 'ACCOUNT' ? 'success' : kind === 'OU' ? 'warning' : 'info')}
          />
          {isExcluded && (
            <Chip label="attached" size="small" sx={treeChipSx('neutral')} />
          )}
          {quota !== undefined && quota >= 0 && (
            <Chip
              label={`${quota}/5`}
              size="small"
              sx={treeChipSx(atCapacity ? 'error' : 'info')}
            />
          )}
        </Box>
      )}
      {hasChildren && (isHidden || expanded) && (
        <Collapse in={isHidden || expanded}>
          {node.children.map((child) => (
            <OrgTreeCheckbox
              key={child.id}
              node={child}
              depth={isHidden ? depth : depth + 1}
              selectedIds={selectedIds}
              onToggle={onToggle}
              excludeIds={excludeIds}
              hideRoot={false}
              quotaMap={quotaMap}
            />
          ))}
        </Collapse>
      )}
    </Box>
  );
};

export default OrgTreeCheckbox;
